// Post-render linker: turns Bible references in answer HTML into
// bibleserver.com links (ADR-0029, ADR-0085 #30). Operates on text nodes only,
// never on attributes; hrefs are built from parsed components, not raw input.
// Book data comes from window.BIBLE_BOOKS (injected by the page routes from
// app/bible_books.json — the single source shared with the backend).

// The translation code comes from the user preference (default ELB),
// injected by the chat route as window.BIBLESERVER_TRANSLATION. An English
// reference links to the ESV whatever the preference (FR-9): bibleserver
// needs the English book name for it (DEV_TECH_DESIGN-0085 D-9).
const DEFAULT_TRANSLATION = 'ELB';
const ENGLISH_TRANSLATION = 'ESV';

function translationCode() {
    return window.BIBLESERVER_TRANSLATION || DEFAULT_TRANSLATION;
}

export function bibleserverLabel(english = false) {
    const code = english ? ENGLISH_TRANSLATION : translationCode();
    return `Auf bibleserver.com öffnen (${code})`;
}

// Mirrors app/services/bible_grammar.py; tests/fixtures/
// bible_reference_cases.json holds both to the same parses (FR-7). Book: an
// optional ordinal, a word and, for English names, one " of <word>". The
// separator decides the language: `,` German only, `:` English first. A
// chapter-crossing range repeats the separator without whitespace (D-10). A
// lookbehind instead of `\b`, which knows no umlaut here. `gap` tells a
// chapter list from chapter and verse (D-25).
const LETTERS = 'A-Za-zÄÖÜäöüß';
const REFERENCE_SOURCE =
    `(?<![${LETTERS}0-9])` +
    `(?<book>(?:[1-3]\\s*\\.?\\s*)?(?<word>[${LETTERS}]+)(?:\\s+of\\s+[A-Za-z]+)?\\.?)` +
    '\\s*(?<chapter>\\d{1,3})\\s*(?<sep>[,:])(?<gap>\\s*)(?<verse>\\d{1,3})' +
    '(?<range>\\s*[-\\u2013\\u2014]\\s*(?:(?<chapterEnd>\\d{1,3})\\k<sep>(?=\\d))?' +
    '(?<verseEnd>\\d{1,3}))?';

let variantMaps = null;

function normalize(bookText) {
    return bookText.replace(/[.\s]/g, '').toLowerCase();
}

function getVariantMaps() {
    if (variantMaps) return variantMaps;
    if (!window.BIBLE_BOOKS) return null;
    variantMaps = { german: new Map(), english: new Map() };
    window.BIBLE_BOOKS.forEach(book => {
        book.variants.forEach(variant => variantMaps.german.set(variant, book));
        (book.variants_en || []).forEach(variant => variantMaps.english.set(variant, book));
    });
    return variantMaps;
}

function isLowerCase(character) {
    return character === character.toLowerCase() && character !== character.toUpperCase();
}

// The book and whether it was read as English, or null (FR-3, FR-6).
function resolveBook(groups) {
    const maps = getVariantMaps();
    if (!maps) return null;
    const key = normalize(groups.book);
    let found = null;
    if (groups.sep === ':' && maps.english.has(key)) {
        found = { book: maps.english.get(key), english: true };
    } else if (maps.german.has(key)) {
        found = { book: maps.german.get(key), english: false };
    }
    const wordLike = found && (found.book.word_like || []).includes(key);
    if (wordLike && isLowerCase(groups.word[0])) return null;
    return found && isEnumeration(groups, found) ? null : found;
}

// An ordinal written with a period, as in "## 2. Joh 21,1" (FR-66, D-26).
const ENUMERATION = /^[1-3]\s*\./;
// After a comma and whitespace, the verse is followed by a further number (FR-65).
const LIST_CONTINUES = /^\s*,\s*\d/;

// Whether the ordinal enumerates rather than numbers the book: the numbered
// book lacks the chapter and the word alone names a book. The retry one
// character later then reads that book (D-26).
function isEnumeration(groups, found) {
    if (!ENUMERATION.test(groups.book)) return false;
    if (Number(groups.chapter) <= found.book.chapters) return false;
    const maps = getVariantMaps();
    return (found.english ? maps.english : maps.german).has(normalize(groups.word));
}

// „Psalm 13, 22, 88“: chapters, not chapter and verse (FR-65, D-25).
function isChapterList(match) {
    const { sep, gap, range } = match.groups;
    if (sep !== ',' || !gap || range !== undefined) return false;
    return LIST_CONTINUES.test(match.input.slice(match.index + match[0].length));
}

// An inverted range is dropped; the reference keeps its start verse.
function rangeOf(groups) {
    const none = { chapterEnd: null, verseEnd: null };
    if (groups.verseEnd === undefined) return none;
    const verseEnd = Number(groups.verseEnd);
    if (groups.chapterEnd !== undefined) {
        const chapterEnd = Number(groups.chapterEnd);
        return chapterEnd > Number(groups.chapter) ? { chapterEnd, verseEnd } : none;
    }
    return verseEnd >= Number(groups.verse) ? { chapterEnd: null, verseEnd } : none;
}

function referenceAt(match) {
    const found = resolveBook(match.groups);
    if (!found || isChapterList(match)) return null;
    const { chapterEnd, verseEnd } = rangeOf(match.groups);
    const rangeLength = verseEnd === null ? (match.groups.range || '').length : 0;
    const end = match.index + match[0].length - rangeLength;
    return {
        book: found.book.name,
        nameEn: found.book.name_en,
        chapter: Number(match.groups.chapter),
        verse: Number(match.groups.verse),
        chapterEnd,
        verseEnd,
        english: found.english,
        start: match.index,
        end,
        raw: match.input.slice(match.index, end).replace(/\s+/g, ' ').trim()
    };
}

/**
 * Every reference in the text, in order. A match whose book is unknown is
 * retried one character further on ("the Gospel of John 3:16", "2. Joh
 * 21,1"); so is a list of chapters, which yields nothing.
 */
export function findReferences(text) {
    const pattern = new RegExp(REFERENCE_SOURCE, 'g');
    const references = [];
    let match;
    while ((match = pattern.exec(text)) !== null) {
        const reference = referenceAt(match);
        if (!reference) {
            pattern.lastIndex = match.index + 1;
            continue;
        }
        references.push(reference);
        pattern.lastIndex = reference.end;
    }
    return references;
}

/** The reference that is the whole (trimmed) string, or null. */
export function parseReference(text) {
    if (!text) return null;
    const trimmed = text.trim();
    const found = findReferences(trimmed);
    if (found.length === 1 && found[0].start === 0 && found[0].end === trimmed.length) {
        return found[0];
    }
    return null;
}

/** Identity of a reference, ignoring its spelling (as Reference.key). */
export function referenceKey(reference) {
    const { book, chapter, verse, chapterEnd, verseEnd, english } = reference;
    return [book, chapter, verse, chapterEnd, verseEnd, english ? 'en' : 'de'].join('|');
}

function rangeSuffix(reference, separator) {
    if (reference.chapterEnd !== null) {
        return `-${reference.chapterEnd}${separator}${reference.verseEnd}`;
    }
    return reference.verseEnd !== null ? `-${reference.verseEnd}` : '';
}

function buildHref(reference) {
    const separator = reference.english ? ':' : ',';
    const book = reference.english ? reference.nameEn : reference.book.replace(/\s+/g, '');
    const passage = `${book}${reference.chapter}${separator}${reference.verse}`
        + rangeSuffix(reference, separator);
    const code = reference.english ? ENGLISH_TRANSLATION : translationCode();
    return `https://www.bibleserver.com/${encodeURIComponent(code)}/${encodeURIComponent(passage)}`;
}

/**
 * Returns the bibleserver.com URL for a single reference string
 * (e.g. "Röm 8,28", "Romans 8:28"), or null when it cannot be resolved.
 */
export function bibleserverUrlFor(reference) {
    const parsed = parseReference(reference);
    return parsed ? buildHref(parsed) : null;
}

function anchorFor(reference, text) {
    const anchor = document.createElement('a');
    anchor.href = buildHref(reference);
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    anchor.title = bibleserverLabel(reference.english);
    anchor.textContent = text;
    return anchor;
}

// `replaceRef(reference, text)` may return a Node to stand instead of the
// link — how answer_checks.js marks a reference that does not resolve.
function linkifyText(text, replaceRef) {
    const references = findReferences(text);
    if (references.length === 0) return null;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    references.forEach(reference => {
        const written = text.slice(reference.start, reference.end);
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, reference.start)));
        const replacement = replaceRef ? replaceRef(reference, written) : null;
        fragment.appendChild(replacement || anchorFor(reference, written));
        lastIndex = reference.end;
    });
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    return fragment;
}

/**
 * Wraps Bible references found in the given (already sanitized) HTML string
 * in bibleserver links. Text inside existing links, code and pre blocks is
 * left untouched. Returns the augmented HTML string.
 */
export function linkBibleReferences(html, options = {}) {
    if (!html || !getVariantMaps()) return html;
    const template = document.createElement('template');
    template.innerHTML = html;

    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement && node.parentElement.closest('a, code, pre, button')) continue;
        textNodes.push(node);
    }
    textNodes.forEach(node => {
        const replacement = linkifyText(node.nodeValue, options.replaceRef);
        if (replacement) node.parentNode.replaceChild(replacement, node);
    });
    return template.innerHTML;
}
